import { ArrowLeftIcon, ArrowRightIcon, Button } from '@iheartjane/reefer';
import type { LabeledOption } from '@iheartjane/types';
import { clsx } from 'clsx';
import { useEffect, useMemo } from 'react';
import type { ParamParseKey, PathMatch } from 'react-router';
import { matchPath, useMatch, useNavigation } from 'react-router';

import { CategorySelectorSkeleton } from '@jane/bloom/components/skeletonLoaders/categorySelectorSkeleton';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import { useScroller } from '@jane/bloom/hooks/useScroller';
import { useSkeletonLoader } from '@jane/bloom/hooks/useSkeletonLoader';
import type { SearchFilterFacet } from '@jane/bloom/viewModels/productSearchFilters';

import { CategoryLink } from './categoryLink';
import styles from './categorySelector.module.css';

type CategorySelectorProps = {
  displayCategories: LabeledOption[];
};

// Prefer the in-flight navigation target over the current location so the
// highlight updates optimistically during a category change.
const useMatchesCurrentOrDestinationLocation = <TMatchPattern extends string>(
  matchPattern: TMatchPattern
): PathMatch<ParamParseKey<TMatchPattern>> | null => {
  const { location } = useNavigation();
  const currentPageMatch = useMatch<TMatchPattern>(matchPattern);
  const destinationPageMatch = matchPath<TMatchPattern>(
    matchPattern,
    location?.pathname ?? ''
  );

  return destinationPageMatch || currentPageMatch;
};

export function CategorySelector({ displayCategories }: CategorySelectorProps) {
  const catScroll = useScroller<HTMLDivElement, HTMLAnchorElement>({
    byRow: false,
    gapWidth: 8,
  });
  const { shouldShowFullSkeletonLoader } = useSkeletonLoader();
  const { shoppingPreferences } = useRootLoaderData();

  const slugCategoryPage = useMatchesCurrentOrDestinationLocation(
    ':storeSlug/:category'
  );
  const localizedCategoryPage = useMatchesCurrentOrDestinationLocation(
    'store/:storeId/:category'
  );
  const categoryPage = useMatchesCurrentOrDestinationLocation(':category');

  const selectedCategory = useMemo(() => {
    return (
      slugCategoryPage?.params.category ??
      localizedCategoryPage?.params.category ??
      categoryPage?.params.category
    );
  }, [categoryPage, localizedCategoryPage, slugCategoryPage]);

  useEffect(() => {
    const categoryIndex = displayCategories.findIndex(
      (category) => selectedCategory && category.value === selectedCategory
    );
    const selectedElement = catScroll.refs.current[categoryIndex];
    const scrollContainer = catScroll.scrollElementRef.current;

    if (selectedElement && scrollContainer) {
      const { offsetLeft: elementOffsetLeft, offsetWidth: elementWidth } =
        selectedElement;
      const { offsetWidth: containerWidth } = scrollContainer;

      const targetScrollLeft =
        elementOffsetLeft - (containerWidth - elementWidth) / 2;

      scrollContainer.scrollTo({
        behavior: 'smooth',
        left: targetScrollLeft,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <nav className={styles['container']} id="category-selector">
      {catScroll.canScrollLeft && (
        <Button.Icon
          ariaLabel="Scroll left"
          className={styles['scrollArrow']}
          icon={<ArrowLeftIcon />}
          variant="minimal"
          onClick={() => {
            catScroll.scrollElementRef.current?.scrollTo({
              behavior: 'smooth',
              left: catScroll.calculateNextLeft(),
            });
          }}
        />
      )}

      <div
        className={clsx(styles['categorySelector'], catScroll.classNames)}
        ref={catScroll.scrollElementRef}
      >
        {shouldShowFullSkeletonLoader ? (
          <CategorySelectorSkeleton />
        ) : (
          displayCategories.map((category: SearchFilterFacet, i: number) => (
            <CategoryLink
              key={category.value}
              ref={(ref) => {
                catScroll.refs.current[i] = ref;
              }}
              reservationMode={shoppingPreferences?.reservationMode}
              defaultCategory={displayCategories[0].value}
              category={category.value}
              label={category.label}
              selected={
                selectedCategory ===
                (category.value === 'brand_specials'
                  ? 'jane-gold'
                  : category.value)
              }
            />
          ))
        )}
      </div>
      {catScroll.canScrollRight && (
        <Button.Icon
          ariaLabel="Scroll right"
          className={styles['scrollArrow']}
          icon={<ArrowRightIcon />}
          variant="minimal"
          onClick={() => {
            catScroll.scrollElementRef.current?.scrollTo({
              behavior: 'smooth',
              left: catScroll.calculateNextRight(),
            });
          }}
        />
      )}
    </nav>
  );
}
